import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Card,
  makeStyles
} from '@material-ui/core';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff
} from '@material-ui/icons';
import Page from 'src/components/Page';
import services from './Services';
import { Alert, AlertTitle } from '@material-ui/lab';
import { LoadingComponent } from './../../utils/newLoader';
import { trackPromise } from 'react-promise-tracker';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#f0f4f0', // fallback
  },
  loginCard: {
    display: 'flex',
    maxWidth: 900,
    width: '100%',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 40px 80px rgba(0, 0, 0, 0.4)',
    position: 'relative',
    zIndex: 1,
  },
  imagePanel: {
    flex: 1,
    position: 'relative',
    backgroundPositionY: 'top',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#d0e0b0', // fallback
  },
  logoContainer: {
    position: 'relative',
    width: 180,
    height: 140,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    margin: '0 auto',
  },
  formPanel: {
    flex: 1,
    backgroundColor: '#ffffff',
    padding: theme.spacing(6, 5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  welcomeText: {
    fontSize: '2.3rem',
    fontWeight: 600,
    color: '#287329',
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  inputField: {
    marginBottom: theme.spacing(2.5),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f8faf7',
      borderRadius: 10,
      border: '1px solid #e8f0e3',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover': {
        backgroundColor: '#f2f7ed',
        '& fieldset': {
          borderColor: 'transparent',
        },
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        border: '1px solid #8fbc4f',
        '& fieldset': {
          borderColor: 'transparent',
        },
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '16px 14px 16px 0',
      fontSize: '0.95rem',
    },
  },
  inputIcon: {
    color: '#9ec571',
    fontSize: '1.3rem',
  },
  loginButton: {
    marginTop: "50px",
    backgroundColor: '#57d82b',
    color: '#ffffff',
    padding: '10px 0',
    fontSize: '18px',
    fontWeight: 600,
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 4px 15px rgba(147, 185, 93, 0.3)',
    '&:hover': {
      backgroundColor: '#287329',
      boxShadow: '0 6px 20px rgba(147, 185, 93, 0.4)',
    },
    '&:disabled': {
      backgroundColor: '#c5d9b0',
      color: '#ffffff',
    },
  },
  alertContainer: {
    marginBottom: theme.spacing(2.5),
    '& .MuiAlert-root': {
      borderRadius: 10,
    },
  },
  [theme.breakpoints.down('sm')]: {
    loginCard: {
      flexDirection: 'column',
      maxWidth: 450,
    },
    imagePanel: {
      minHeight: 200,
    },
    logoContainer: {
      width: 100,
      height: 100,
    },
    formPanel: {
      padding: theme.spacing(4, 3),
    },
  },
}));

const getImagePath = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('/')) return path;
  return '/' + path;
};

const LoginView = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [messageModel, setmessageModel] = useState([]);
  const [logoConfig, setLogoConfig] = useState({
    LogoUrl: '/RajjuruwaththaLogo.png',
    CompanyName: '',
    CardImage: '/CardImage.jpg',
    BackgroundImage: '/backgroundImage.jpg'
  });
  const [configLoaded, setConfigLoaded] = useState(false);
  const [imageErrors, setImageErrors] = useState({ background: false, card: false, logo: false });

  useEffect(() => {
    fetch('/webConfiguration.json')
      .then(response => response.json())
      .then(data => {
        if (data.LogoSetting) {
          const newConfig = {
            LogoUrl: getImagePath(data.LogoSetting.LogoUrl) || logoConfig.LogoUrl,
            CompanyName: data.LogoSetting.CompanyName || '',
            CardImage: getImagePath(data.LogoSetting.CardImage) || logoConfig.CardImage,
            BackgroundImage: getImagePath(data.LogoSetting.BackgroundImage) || logoConfig.BackgroundImage,
          };
          setLogoConfig(newConfig);
        }
        setConfigLoaded(true);
      })
      .catch(error => {
        console.error('Error loading configuration, using default images:', error);
        setConfigLoaded(true); 
      });
  }, []); 

  useEffect(() => {
    if (!configLoaded) return;

    const preloadImage = (src, type) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
      img.onload = () => setImageErrors(prev => ({ ...prev, [type]: false }));
      img.onerror = () => setImageErrors(prev => ({ ...prev, [type]: true }));
    };

    preloadImage(logoConfig.BackgroundImage, 'background');
    preloadImage(logoConfig.CardImage, 'card');
    preloadImage(logoConfig.LogoUrl, 'logo');
  }, [configLoaded, logoConfig]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  async function login(values) {
    let result = await services.login(values);
    if (result.statusCode === "Error") {
      setIsHidden(true);
      setmessageModel(result.message);
      return;
    }
    sessionStorage.setItem('token', result.data);
    navigate('/loader');
  }

  const rootBackgroundStyle = imageErrors.background || !logoConfig.BackgroundImage
    ? { backgroundColor: '#e0e8e0' }
    : { backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url(${logoConfig.BackgroundImage})` };

  const cardImageStyle = imageErrors.card || !logoConfig.CardImage
    ? { backgroundColor: '#c8dcc8' }
    : { backgroundImage: `url(${logoConfig.CardImage})` };

  const logoStyle = imageErrors.logo || !logoConfig.LogoUrl
    ? { backgroundColor: '#ffffff' }
    : { backgroundImage: `url(${logoConfig.LogoUrl})` };

  if (!configLoaded) {
    return (
      <Page className={classes.root} title="Sign in">
        <LoadingComponent />
      </Page>
    );
  }

  return (
    <Page
      className={classes.root}
      title="Sign in"
      style={rootBackgroundStyle}
    >
      <LoadingComponent />
      <Card className={classes.loginCard} elevation={0}>
        <Box
          className={classes.imagePanel}
          style={cardImageStyle}
        />
        <Box className={classes.formPanel}>
          <Box
            className={classes.logoContainer}
            style={logoStyle}
          />
          <Typography className={classes.welcomeText}>
            Welcome !
          </Typography>
          <Typography style={{
            color: '#08b00b',
            fontSize: '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '20px',
          }}>
            Please enter your credentials to access your account.
          </Typography>
          <Formik
            initialValues={{
              username: "",
              password: ""
            }}
            validationSchema={Yup.object().shape({
              username: Yup.string().max(255).required('Username is required'),
              password: Yup.string().max(255).required('Password is required')
            })}
            onSubmit={(e) => trackPromise(login(e))}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                {isHidden && (
                  <Box className={classes.alertContainer}>
                    <Alert severity="error">
                      <AlertTitle>
                        Error: <strong>{messageModel}</strong>
                      </AlertTitle>
                    </Alert>
                  </Box>
                )}
                <TextField
                  error={Boolean(touched.username && errors.username)}
                  fullWidth
                  helperText={touched.username && errors.username}
                  placeholder="username"
                  margin="none"
                  name="username"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.username}
                  variant="outlined"
                  className={classes.inputField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon className={classes.inputIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  placeholder="Password"
                  margin="none"
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  variant="outlined"
                  className={classes.inputField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon className={classes.inputIcon} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  className={classes.loginButton}
                  disabled={isSubmitting}
                  fullWidth
                  type="submit"
                  variant="contained"
                >
                  LOGIN
                </Button>
              </form>
            )}
          </Formik>
        </Box>
      </Card>
    </Page>
  );
};

export default LoginView;